(ns app.front.view.application-wizard.summary
  (:require ["@carbon/icons-react/es/chevron--down/16" :default ChevronDown16]
            ["@carbon/icons-react/es/chevron--up/16" :default ChevronUp16]
            ["@carbon/icons-react/es/information/16" :default Information16]
            [app.domain.attachment.model :as attachment-model]
            [app.domain.fee.model :as fee-model]
            [app.domain.form.model :as form-model]
            [app.domain.group.model :as group-model]
            [app.domain.party.model :as party-model]
            [app.domain.util :as du]
            [app.domain.workspace.model :as workspace-model]
            [app.front.entities :as entities]
            [app.front.form.subs :as form-subs]
            [app.front.process :as process]
            [app.front.requirement.subs :as req-subs]
            [app.front.ui :as ui]
            [app.front.ui.chat-panel :as chat-panel]
            [app.front.ui.error-list :as ui-error-list]
            [app.front.ui.panels :as panels]
            [app.front.ui.v2.status-tag :as status-tag]
            [app.front.util :as futil :refer [for-all for-frag]]
            [app.front.view.application-wizard.attachments :as attachments-step]
            [app.front.view.application-wizard.forms :as forms-step]
            [app.front.view.application-wizard.subs :as subs]
            [app.front.view.bond.workspace-section :as bond-section]
            [app.front.view.party.common :as party-common]
            [app.front.view.party.subs :as party-subs]
            [app.front.view.workspace.subs :as ws-subs]
            [app.i18n.model :as i18n-model]
            [app.process.model :as process-model]
            [clojure.string :as str]
            [core.front.carbon :as cc]
            [core.front.date-util :as date-util]
            [core.front.event :refer [<sub >evt]]
            [core.front.feature-flags :as ff]
            [core.front.href :as href]
            [core.front.icons :as i]
            [re-frame.core-instrumented :as rf]
            [reagent.core :as r]))

(defn- config-names [config lang]
  (some-> config :group.config/names (i18n-model/get-text lang)))

(defn- join-names [names]
  (some->> names (remove str/blank?) seq (str/join ", ")))

(defn- current-party? [party]
  (or (party-model/active? party)
      (party-model/pending? party)))

(defn- party-role-names [party]
  (->> (:party/party-roles party)
       (map party-common/party-role-label)
       sort
       join-names))

(rf/reg-sub ::project-name
  :<- [::ws-subs/workspace]
  (fn [workspace _]
    (-> workspace workspace-model/project :project/name)))

(rf/reg-sub ::description-of-work
  :<- [::ws-subs/workspace]
  :-> :workspace/description-of-work)

(rf/reg-sub ::address
  :<- [::ws-subs/workspace]
  :<- [:language]
  :<- [:tr]
  (fn [[workspace lang tr] _]
    (workspace-model/full-address-with-units workspace lang tr)))

(rf/reg-sub ::property-id
  :<- [::ws-subs/workspace]
  (fn [workspace _]
    (-> workspace workspace-model/single-property :property/id)))

(rf/reg-sub ::category
  :<- [::ws-subs/application]
  :<- [::ws-subs/category-group-config]
  :<- [:language]
  (fn [[application ws-category lang] _]
    (config-names (or (:application/category-group-config application) ws-category) lang)))

(rf/reg-sub ::work-type
  :<- [::ws-subs/application]
  :<- [::ws-subs/workspace]
  :<- [:language]
  (fn [[application workspace lang] _]
    (join-names
      (map #(config-names % lang)
           (or (:application/work-type-group-configs application)
               (:workspace/work-type-group-configs workspace))))))

(rf/reg-sub ::work-target
  :<- [::ws-subs/application]
  :<- [::ws-subs/workspace]
  :<- [:language]
  (fn [[application workspace lang] _]
    (config-names (or (:application/work-target-group-config application)
                      (:workspace/work-target-group-config workspace))
                  lang)))

;; Upfront & deposit costs --------------------------------------------------
;; The upfront and deposit bills are created (in `:bill.state/unpaid`) when the
;; application is created, as children of the application phase entity. We read
;; them straight off the application entity graph (the bills are streamed into
;; the entity-db by the create-application response).

(defn upfront-and-deposit-bills [application]
  (->> application
       fee-model/phase-bills
       (filter #(#{:bill.phase/upfront :bill.phase/deposit} (:bill/phase %)))
       (sort-by :entity/created)))

(rf/reg-sub ::upfront-and-deposit-bills
  :<- [::ws-subs/application]
  :-> upfront-and-deposit-bills)

(rf/reg-sub ::currency
  :<- [::ws-subs/authority-group]
  :-> :group/currency)


(rf/reg-event-fx ::check-rules
  (fn [_ [_ application-id force?]]
    (when application-id
      {:dispatch [:comm/command-monitored
                  {:command    :application/check-wizard-ai-rules
                   :data       (cond-> {:application-id application-id}
                                 force? (assoc :force? true))
                   :on-success [::check-rules-success]}]})))

(rf/reg-event-fx ::check-rules-success
  (fn [_ [_ status response]]
    {:dispatch [::entities/apply-changes status response]}))

(defn- current-application [db]
  (let [ctx          (entities/db->context db)
        workspace-id (or (-> db :route/match :parameters :path :workspace-id)
                         (-> db :route/match :parameters :query :workspace-id)
                         (-> db :view/workspace :workspace/id))]
    (when workspace-id
      (some-> (entities/find-entity ctx {:workspace/id workspace-id})
              workspace-model/workspace-application))))

;; Permit bill estimate ------------------------------------------------------
;; The permit bill does not exist while the application is in draft - it is only
;; generated (as a draft bill) at/after submission. To still show its expected
;; cost in the wizard summary, we ask the backend to resolve the permit-phase
;; fees for the current application *without persisting anything* and render the
;; returned lightweight, already-localized estimate.

(rf/reg-event-fx ::fetch-permit-estimate
  (fn [_ [_ application-id]]
    (when application-id
      {:comm/command {:command    :fee/estimate-permit-bill
                      :data       {:phase-id application-id}
                      :method     :get
                      :on-success [::fetch-permit-estimate-success application-id]}})))

(rf/reg-event-db ::fetch-permit-estimate-success
  (fn [db [_ application-id _status estimate]]
    (assoc-in db [::permit-estimates application-id] estimate)))

(rf/reg-event-fx ::maybe-fetch-permit-estimate
  (fn [{:keys [db]} _]
    (let [application          (current-application db)
          application-id       (:db/id application)
          show-estimate-costs? (group-model/has-feature? (:domain/authority-group (:entity/parent application))
                                                         :group.feature/show-wizard-estimated-costs)]
      (when (and application-id
                 (ff/enabled? :feature-flag.id/wizard-estimate-costs)
                 show-estimate-costs?
                 (= :state.application/draft (:domain/state application)))
        {:dispatch [::fetch-permit-estimate application-id]}))))

(rf/reg-sub ::permit-estimates :-> ::permit-estimates)

(rf/reg-sub ::permit-estimate
  :<- [::ws-subs/application]
  :<- [::permit-estimates]
  (fn [[application estimates] _]
    (get estimates (:db/id application))))

(rf/reg-sub ::rule-check-completed?
  :<- [::ws-subs/application]
  (fn [application _]
    (boolean (:application/wizard-ai-rule-check-completed application))))

(rf/reg-sub ::rule-check-findings
  :<- [::rule-check-completed?]
  :<- [::ws-subs/application]
  (fn [[rule-check-completed? application] _]
    (when rule-check-completed?
      (->> (:application/wizard-ai-rule-check-findings application)
           (sort-by :db/id)
           vec))))

(rf/reg-sub ::rule-check-in-flight?
  :<- [:comm/command-processing? :application/check-wizard-ai-rules]
  (fn [checking? _]
    checking?))

(defn- findings-count-text [tr findings]
  (let [n (count findings)]
    (tr [(if (= 1 n)
           :application-wizard/cloudcheck-analysis-finding-count
           :application-wizard/cloudcheck-analysis-findings-count)]
        [n])))

(defn- finding-message [finding]
  (str/join " "
            (remove str/blank?
                    [(:wizard-ai-rule-check-finding/applicant-message finding)
                     (:wizard-ai-rule-check-finding/evidence finding)])))

(defn- guide-message [findings]
  (str "Guide me to fix these CloudCheck issues:\n"
       (->> findings
            (map-indexed (fn [idx finding]
                           (str (inc idx) ". " (finding-message finding))))
            (str/join "\n"))))

(defn- cloudcheck-header [tr checking? findings expanded? toggle-expanded]
  (let [completed? (<sub [::rule-check-completed?])
        content    [:div.flex.items-center.gap-4.px-4.py-5.md:px-8
                    (when-not checking?
                      [:span.text-ui-violet.shrink-0
                       [:> (if expanded? ChevronUp16 ChevronDown16)]])
                    [:span.w-12.h-12.rounded-full.bg-orange-400.text-ui-night-sky-100.flex.items-center.justify-center.shrink-0
                     (if checking?
                       (cc/loading-spinner {:small true :with-overlay false})
                       [cc/icon {:icon        i/nova
                                 :description ""
                                 :class-name  "fill-current"
                                 :width       "24"
                                 :height      "24"}])]
                    [:span.flex.flex-col.text-ui-night-sky-100.leading-tight
                     [:span.font-bold.text-lg
                      (tr [(cond
                             checking? :application-wizard/cloudcheck-analysis-running
                             (not completed?) :application-wizard/cloudcheck-analysis-optional
                             :else :application-wizard/cloudcheck-analysis-results)])]
                     [:span.text-base.font-normal.mt-1
                      (cond
                        checking? (tr [:application-wizard/cloudcheck-analysis-loading])
                        (seq findings) (findings-count-text tr findings)
                        (some? findings) (tr [:application-wizard/cloudcheck-analysis-no-findings]))]]]]
    (if checking?
      [:div.w-full.bg-white content]
      [:button.w-full.bg-white.border-0.p-0.cursor-pointer.text-left.hover:bg-ui-light-gray
       {:type          "button"
        :aria-expanded expanded?
        :aria-controls "cloudcheck-results-body"
        :on-click      toggle-expanded}
       content])))

(defn- cloudcheck-result-actions [tr application-id findings chat-enabled? check-allowed? close]
  [:div.flex.flex-col.sm:flex-row.justify-center.items-stretch.sm:items-center.gap-3.mt-8
   (when (seq findings)
     (cc/button {:kind       :primary
                 :class-name "uppercase"
                 :disabled   (not chat-enabled?)
                 :on-click   #(do
                                (>evt [::panels/open :chat])
                                (>evt [::chat-panel/send-message (guide-message findings)]))}
                (tr [:application-wizard/cloudcheck-analysis-guide-me-to-fix-these])))
   (when check-allowed?
     (cc/button {:kind       :secondary
                 :class-name "uppercase"
                 :disabled   (nil? application-id)
                 :on-click   #(>evt [::check-rules application-id true])}
                (tr [:application-wizard/cloudcheck-analysis-recheck])))
   (cc/button {:kind       :tertiary
               :class-name "uppercase"
               :on-click   close}
              (tr [:close]))])

(defn- cloudcheck-run-actions [tr application-id close]
  [:div.flex.flex-col.sm:flex-row.justify-center.items-stretch.sm:items-center.gap-3.mt-8
   [ui/button {:kind       :primary
               :class-name "uppercase"
               :disabled   (nil? application-id)
               :on-click   #(>evt [::check-rules application-id])}
    (tr [:application-wizard/cloudcheck-analysis-run])]
   [ui/button {:kind       :secondary
               :class-name "uppercase"
               :on-click   close}
    (tr [:close])]])

(defn cloudcheck-results-view [tr application-id checking? findings check-allowed? expanded? toggle-expanded close]
  (let [chat-enabled? (<sub [::chat-panel/chat-enabled?])]
    [:div#cloudcheck-results-view.rounded.border-solid.border-2.border-orange-400.mb-4.overflow-hidden.bg-white
     [cloudcheck-header tr checking? findings expanded? toggle-expanded]
     [:p.px-4.pb-4.md:px-8.text-base.leading-normal.text-ui-night-sky-100
      (tr [:application-wizard/cloudcheck-analysis-disclaimer])]
     (when (and expanded? (not checking?))
       [:div#cloudcheck-results-body.border-t.border-solid.border-gray-300.px-4.py-8.md:px-8.text-ui-night-sky-100
        (cond
          (seq findings)
          [:div.text-base.leading-normal
           [:div (tr [:application-wizard/cloudcheck-analysis-summary])]
           [:ul.list-disc.pl-8.mt-1.mb-8
            (for-all [finding findings]
              ^{:key (:db/id finding)}
              [:li (finding-message finding)])]
           [:p (tr [:application-wizard/cloudcheck-analysis-delay-warning])]
           [cloudcheck-result-actions tr application-id findings chat-enabled? check-allowed? close]]

          (some? findings)
          [:div
           [:p (tr [:application-wizard/cloudcheck-analysis-no-findings])]
           [cloudcheck-result-actions tr application-id findings chat-enabled? check-allowed? close]]

          check-allowed?
          [:div
           [:p (tr [:application-wizard/cloudcheck-analysis-run-description])]
           [cloudcheck-run-actions tr application-id close]])])]))

(defn cloudcheck-results [_tr]
  (let [expanded? (r/atom true)]
    (fn [tr]
      (let [application-id (some-> (<sub [::ws-subs/application]) :db/id)
            findings       (<sub [::rule-check-findings])
            checking?      (<sub [:comm/command-processing? :application/check-wizard-ai-rules])
            check-allowed? (<sub [::subs/check-wizard-ai-rules-allowed?])]
        (when (or check-allowed? (some? findings) checking?)
          [cloudcheck-results-view tr application-id checking? findings check-allowed? @expanded?
           #(swap! expanded? not)
           #(reset! expanded? false)])))))


(rf/reg-event-fx ::go-to-forms-step
  (fn [_ [_ form-id]]
    {:fx [[:dispatch [::forms-step/expand-form form-id]]
          [:dispatch [:app.front.view.application-wizard.core/open-step {:step-path "forms"}]]]}))

(rf/reg-event-fx ::go-to-attachments-step
  (fn [_ _]
    {:dispatch [:app.front.view.application-wizard.core/open-step {:step-path "attachments"}]}))

(defn row-with-status
  ([status-color left-text right-text] (row-with-status status-color left-text right-text nil))
  ([status-color left-text right-text on-click]
   [:div.rounded-md.overflow-hidden.relative.bg-ui-light-gray.mb-1
    (when on-click
      {:class       "cursor-pointer hover:bg-gray-200"
       :role        "button"
       :tab-index   0
       :on-click    on-click
       :on-key-down (fn [e] (when (futil/enter-press? e) (on-click e)))})
    [:div.absolute.left-0.top-0.bottom-0.w-2.rounded-l-md {:class status-color}]
    [:div.grid.items-center.px-6.py-3
     {:class "grid-cols-[1fr_1fr]"}
     [:span left-text]
     [:span right-text]]]))

(defn form-row [tr form {:keys [renewal-overview?]}]
  (let [form-id  (:db/id form)
        status   (<sub [::form-subs/form-wizard-status-color-code form-id])
        missing? (<sub [::form-subs/form-missing-mandatory-fields? form-id])]
    [row-with-status
     (case status :attention "bg-status-attention" :warning "bg-orange-400" :success "bg-green-600" "bg-gray-300")
     (form-model/form-label form (<sub [:language]))
     (cond
       missing?              (tr [:application-wizard/incomplete])
       (= status :attention) (tr [:wizard/forms-confirmation-needed])
       :else                 "")
     (when-not renewal-overview?
       #(>evt [::go-to-forms-step form-id]))]))

(defn overview-forms [tr application {:keys [renewal-overview?] :as opts}]
  (let [all-confirmed? (<sub [::form-subs/all-wizard-forms-confirmed? application])]
    [:div#overview-forms.py-2
     [:h5.pb-2 (tr [:application-wizard/forms])]
     (when (and (not renewal-overview?) (not all-confirmed?))
       [:p.text-orange-600.text-sm.mb-2 (tr [:wizard/forms-unconfirmed-warning])])
     (for-all [form (<sub [::form-subs/application-forms application])]
       ^{:key (:db/id form)}
       [form-row tr form opts])]))

(defn attachment-row [satisfied? type-label filename on-click]
  [row-with-status (if satisfied? "bg-green-600" "bg-orange-400") type-label filename on-click])

(defn hanging-attachments-warning
  "Points the user at the attachments step to deal with files that aren't attached to anything yet.
   Not shown in the renewal overview, which is read-only and has no link to navigate from."
  [tr {:keys [renewal-overview?]}]
  (when-not renewal-overview?
    (let [workspace-id (<sub [::subs/workspace-id])]
      [:div.flex.items-center.gap-3.mb-3
       [cc/icon {:icon        i/circle-info
                 :description ""
                 :aria-hidden true
                 :fill        "var(--status-attention)"}]
       [:p.p-0
        (tr [:application-wizard/attachments-hanging-files-warning-before-link])
        [:a.bx--link
         {:href (href/href :view/application-wizard-workspace
                           {:workspace-id workspace-id
                            :step         "attachments"})}
         (tr [:application-wizard/attachments])]
        (tr [:application-wizard/attachments-hanging-files-warning-after-link]
            [(tr [:application-wizard/attachments-create])])]])))

(defn overview-attachments [tr application {:keys [renewal-overview?] :as opts}]
  (let [requirements (<sub [::req-subs/all-attachment-requirements (:db/id application)])
        attachments  (<sub [::attachments-step/attachments (:db/id application)])]
    [:div#overview-attachments.pt-2
     [:h5.pb-2 (tr [:application-wizard/attachments])]
     (when (and (empty? requirements) (empty? attachments))
       [:p.p-0.pl-2 "-"])
     (when (<sub [::attachments-step/hanging-files?])
       [hanging-attachments-warning tr opts])
     ; First the missing required attachments
     (for-all [requirement requirements]
       (let [att-type   (:requirement/attachment-type requirement)
             satisfied? (<sub [::attachments-step/requirement-satisfied-by-attachments? (:db/id application) (:db/id att-type)])]
         (when-not satisfied?
           ^{:key (:db/id requirement)}
           [attachment-row satisfied? (:requirement/title requirement) (tr [:application-wizard/required-attachment-missing])
            (when-not renewal-overview?
              #(>evt [::go-to-attachments-step]))])))
     ; Then all the uploaded attachments
     (for-all [attachment attachments]
       (let [filename   (some-> attachment :attachment/versions first :attachment.version/files first :attachment.version.file/filename)
             type-label (attachment-model/attachment-title (<sub [:language]) attachment)]
         ^{:key (:db/id attachment)}
         [attachment-row true type-label filename
          (when-not renewal-overview?
            #(>evt [::go-to-attachments-step]))]))]))

(def hr [:hr.border-none.bg-gray-300 {:class "h-[1px]"}])

(defn overview-p [text]
  [:p.pl-2.pt-2 (or text "-")])

(defn overview-view
  ([tr] (overview-view tr {}))
  ([tr opts]
   (let [application            (<sub [::ws-subs/application])
         name                   (<sub [::project-name])
         description            (<sub [::description-of-work])
         category               (<sub [::category])
         work-type              (<sub [::work-type])
         work-target            (<sub [::work-target])
         address                (<sub [::address])
         property-id            (<sub [::property-id])
         lang                   (<sub [:language])
         parties                (<sub [::party-subs/parties (:db/id application)])
         manual-submission-date (when (:application/submitted-set-manually? application)
                                  (:application/submitted application))
         format-date-time       (<sub [::date-util/date-time-fn])]
     [:div#overview-view.w-full.rounded.border-solid.border.border-gray-300.p-4.mb-4
      [:h4.mb-4 (tr [:application-type])]
      hr
      [:div.pt-2
       (when name
         [:div [:h5 (tr [:application-wizard/plan-project-name])] [overview-p name]])
       [:div [:h5 (tr [:application-wizard/plan-description-of-work])] [overview-p description]]
       [:div.flex
        [:div.pr-6 [:h5 (tr [:application-wizard/category])] [overview-p category]]
        (when work-type
          [:div.pr-6 [:h5 (tr [:application/work-type])] [overview-p work-type]])
        (when work-target
          [:div.pr-6 [:h5 (tr [:application/work-target])] [overview-p work-target]])]]
      hr
      (when (or address property-id)
        [:<>
         [:div.flex.pt-2
          (when address
            [:div.pr-6 [:h5 (tr [:applications/address])] [overview-p address]])
          (when property-id
            [:div.pr-6 [:h5 (tr [:property/pin])] [overview-p property-id]])]
         hr])
      (when manual-submission-date
        [:<>
         [:div.flex.pt-2
          [:div.pr-6 [:h5 (tr [:application-wizard/application-already-submitted])]
           [overview-p (format-date-time manual-submission-date)]]]
         hr])
      [:div.flex.flex-wrap.gap-6.pt-2
       (for-all [party (filter current-party? parties)]
         ^{:key (:db/id party)}
         [:div.min-w-0
          [:h5 (party-role-names party)]
          [overview-p (party-model/corporation-and-full-name-or-full-name party lang)]])]
      hr
      [overview-forms tr application opts]
      hr
      [overview-attachments tr application opts]])))

(defn group-fees-by-fee-group
  "Groups fees by their fee group entity. Returns a seq of maps
   {:fee-group group-entity-or-nil :rows fees :subtotal sum-of-amounts}, sorted by
   group title. Fees without a group are collected under a `:fee-group` of nil."
  [lang tr fees]
  (->> fees
       (sort-by #(fee-model/fee-title % lang tr))
       (group-by :fee-template/group)
       (sort-by #(fee-model/fee-group-title (first %) lang))
       (map (fn [[fee-group rows]]
              {:fee-group fee-group
               :rows      rows
               :subtotal  (transduce (keep :fee/amount) + 0 rows)}))))

(defn fee-line [tr lang fee amount-str]
  [:div.grid.items-center.px-6.py-2
   {:class "grid-cols-[40px_1fr_1fr]"}
   [:div]
   [:span (fee-model/fee-title fee lang tr)]
   [:span.font-mono.text-right amount-str]])

(defn fee-item-row
  "A non-expandable row for a single fee that does not belong to a fee group."
  [tr lang currency paid? fee]
  [:div.rounded-md.overflow-hidden.relative.bg-white.border.border-solid.border-gray-300
   [:div.absolute.left-0.top-0.bottom-0.w-2.rounded-l-md
    {:class (if paid? "bg-green-600" "bg-orange-300")}]
   [:div.grid.items-center.px-6.py-3
    {:class "grid-cols-[40px_1fr_1fr]"}
    [:div]
    [:span (fee-model/fee-title fee lang tr)]
    [:span.font-mono.text-right (futil/money-string (:fee/amount fee) currency)]]])

(defn fee-group-row
  "A collapsible row for a fee group, showing its subtotal and, when expanded, the
   individual fees it contains."
  [_tr _lang _currency paid? _fee-group]
  (r/with-let [*expanded? (r/atom (not paid?))]
    (fn [tr lang currency paid? {:keys [fee-group rows subtotal]}]
      (let [expanded? @*expanded?]
        [:div.rounded-md.overflow-hidden.relative.bg-white.border.border-solid.border-gray-300
         [:div.absolute.left-0.top-0.bottom-0.w-2.rounded-l-md
          {:class (if paid? "bg-green-600" "bg-orange-300")}]
         ;; Summary row
         [:div.grid.items-center.px-6.py-3.cursor-pointer.select-none
          {:class    "grid-cols-[40px_1fr_1fr]"
           :on-click #(swap! *expanded? not)}
          [:div
           (cc/icon {:icon        (if expanded? i/chevron-up i/chevron-down)
                     :description (fee-model/fee-group-title fee-group lang)})]
          [:span.font-bold (fee-model/fee-group-title fee-group lang)]
          [:span.font-mono.font-bold.text-right (futil/money-string subtotal currency)]]
         ;; Expandable body
         (when expanded?
           [:div.pb-2
            [:hr.border-none.bg-gray-300.mb-2.mx-6 {:class "h-[1px]"}]
            (for-all [fee rows]
              ^{:key (:db/id fee)}
              [fee-line tr lang fee (futil/money-string (:fee/amount fee) currency)])])]))))

(defn bill-view [tr bill]
  (let [lang             (<sub [:language])
        currency         (<sub [::currency])
        fees             (:bill/fees bill)
        total            (fee-model/bill-amount bill)
        groups           (group-fees-by-fee-group lang tr fees)
        {grouped   false
         ungrouped true} (group-by #(nil? (:fee-group %)) groups)
        ungrouped-fees   (mapcat :rows ungrouped)
        paid?            (du/in-state? :bill.state/paid bill)]
    [:div.mb-4
     {:id (str "bill-view-" (:db/id bill))}
     hr
     [:h5.my-4 (str (tr [(:bill/phase bill)]) (when paid? (str " (" (tr [:bill.state/paid]) ")")))]
     [:div.flex.flex-col.gap-2
      ;; Header
      [:div.grid.items-center.rounded-md.px-6.py-3.font-bold.bg-gray-200
       {:class "grid-cols-[40px_1fr_1fr]"}
       [:div]
       [:div (tr [:fees/fee-type])]
       [:div.text-right (tr [:fees/amount])]]
      ;; Group rows
      (for-all [{:keys [fee-group] :as group} grouped]
        ^{:key (str "fee-group-" (:db/id fee-group))}
        [fee-group-row tr lang currency paid? group])
      ;; Ungrouped fee rows
      (for-all [fee ungrouped-fees]
        ^{:key (:db/id fee)}
        [fee-item-row tr lang currency paid? fee])
      [:div.grid.items-center.rounded-md.px-6.py-3.font-bold.bg-white.border.border-solid.border-gray-300
       {:class "grid-cols-[40px_1fr_1fr]"}
       [:div]
       [:span (tr [:fees/total])]
       [:span.font-mono.text-right (futil/money-string total currency)]]]]))


;; --- Permit bill estimate rendering ---------------------------------------
;; Unlike `bill-view`, the estimate is plain, already-localized data returned by
;; `:fee/estimate-permit-bill` (nothing is persisted), so it has its own small
;; renderer instead of relying on resolved entity refs.

(defn estimate-fee-line [currency {:keys [title amount]}]
  [:div.grid.items-center.px-6.py-2
   {:class "grid-cols-[40px_1fr_1fr]"}
   [:div]
   [:span title]
   [:span.font-mono.text-right (futil/money-string amount currency)]])

(defn estimate-group-row [currency {:keys [title subtotal fees]}]
  (r/with-let [*expanded? (r/atom true)]
    (let [expanded? @*expanded?]
      [:div.rounded-md.overflow-hidden.relative.bg-white.border.border-solid.border-gray-300
       [:div.absolute.left-0.top-0.bottom-0.w-2.rounded-l-md.bg-orange-300]
       [:div.grid.items-center.px-6.py-3.cursor-pointer.select-none
        {:class    "grid-cols-[40px_1fr_1fr]"
         :on-click #(swap! *expanded? not)}
        [:div
         (cc/icon {:icon        (if expanded? i/chevron-up i/chevron-down)
                   :description title})]
        [:span.font-bold title]
        [:span.font-mono.font-bold.text-right (futil/money-string subtotal currency)]]
       (when expanded?
         [:div.pb-2
          [:hr.border-none.bg-gray-300.mb-2.mx-6 {:class "h-[1px]"}]
          (for-frag [fee fees]
            [estimate-fee-line currency fee])])])))

(defn estimate-item-row [currency {:keys [title amount]}]
  [:div.rounded-md.overflow-hidden.relative.bg-white.border.border-solid.border-gray-300
   [:div.absolute.left-0.top-0.bottom-0.w-2.rounded-l-md.bg-orange-300]
   [:div.grid.items-center.px-6.py-3
    {:class "grid-cols-[40px_1fr_1fr]"}
    [:div]
    [:span title]
    [:span.font-mono.text-right (futil/money-string amount currency)]]])

(defn estimated-costs-view [tr {:keys [total groups]}]
  (let [currency         (<sub [::currency])
        {grouped   false
         ungrouped true} (group-by #(nil? (:title %)) groups)
        ungrouped-fees   (mapcat :fees ungrouped)]
    [:div.w-full.rounded.border-solid.border.border-gray-300.p-4.mb-2
     [:h4.mb-2 (tr [:application-wizard/estimated-costs])]
     [:p.text-sm.mb-4 (tr [:application-wizard/estimated-costs-disclosure])]
     hr
     [:h5.my-4 (tr [:bill.phase/permit])]
     [:div.flex.flex-col.gap-2
      [:div.grid.items-center.rounded-md.px-6.py-3.font-bold.bg-gray-200
       {:class "grid-cols-[40px_1fr_1fr]"}
       [:div]
       [:div (tr [:fees/fee-type])]
       [:div.text-right (tr [:fees/amount])]]
      (for-frag [group grouped]
        [estimate-group-row currency group])
      (for-frag [fee ungrouped-fees]
        [estimate-item-row currency fee])
      [:div.grid.items-center.rounded-md.px-6.py-3.font-bold.bg-white.border.border-solid.border-gray-300
       {:class "grid-cols-[40px_1fr_1fr]"}
       [:div]
       [:span (tr [:fees/total])]
       [:span.font-mono.text-right (futil/money-string total currency)]]]]))

(defn upfront-costs-view [tr bills]
  [:div.w-full.rounded.border-solid.border.border-gray-300.p-4.mb-2
   [:h4.mb-4 (tr [:application-wizard/upfront-payments])]
   [:<>
    (for-all [bill bills]
      ^{:key (:db/id bill)}
      [bill-view tr bill])]])

(defn bills-view [tr]
  (let [upfront-bills  (<sub [::upfront-and-deposit-bills])
        show-upfront?  (not (empty? upfront-bills))
        estimate       (<sub [::permit-estimate])
        show-estimate? (and (ff/enabled? :feature-flag.id/wizard-estimate-costs)
                            (<sub [::ws-subs/has-feature? :group.feature/show-wizard-estimated-costs])
                            (seq (:groups estimate)))]
    (when (or show-upfront? show-estimate?)
      [:div#bills-view.w-full
       (when show-upfront? [upfront-costs-view tr upfront-bills])
       (when show-estimate? [estimated-costs-view tr estimate])])))

(rf/reg-sub ::bond-required-before-submit?
  :<- [::process/application-process]
  (fn [process _]
    (boolean
      (some (fn [signal]
              (contains? (process-model/rejected-by-guards process [signal])
                         :process.guard/bond-requirement-is-fulfilled?))
            [:process.signal/submit :process.signal/to-pre-submit-payment]))))

(defn bond-section-view [tr]
  (let [application (<sub [::ws-subs/application])]
    (when (<sub [::bond-section/workspace-bonds-section-visible? application])
      (let [required-before-submit? (<sub [::bond-required-before-submit?])]
        [:div#bond-section.w-full.rounded.border-solid.border.border-gray-300.p-4.mb-2
         {:data-guide-id          "bond-section"
          :data-guide-label       (tr [:workspace/bonds])
          :data-guide-description "Associate an existing bond or create a new bond application"}
         [:div.flex.items-start.justify-between.gap-4.mb-4
          [:h4.mb-0 (tr [:workspace/bonds])]
          (when required-before-submit?
            [status-tag/tag {:status :attention} (tr [:application-wizard/bond-required])])]
         (when required-before-submit?
           [:div.flex.items-start.rounded-md.bg-ui-light-gray.p-3.mb-4.p-6
            [:span.mr-2 [:> Information16 {:aria-hidden "true"}]]
            [:span (tr [:application-wizard/bond-required-info])]])
         [bond-section/bonds-content {:tr tr :fsm-target application :wizard? true}]]))))

(defn submit-guard-results
  ([tr] (submit-guard-results tr {}))
  ([tr {:keys [heading?] :or {heading? true}}]
   (let [{:keys [status errors]} (<sub [::subs/submit-action])]
     (when (and (= :disabled status) (not-empty errors))
       [:div.flex.flex-col.lg:flex-row.justify-between.mt-4
        (when heading?
          [:p (tr [:application/incomplete-tasks-exist])])
        [:div#error-list.flex.flex-col
         [ui-error-list/signal-error-list tr errors]]]))))

(defn view [tr]
  (let [application         (<sub [::ws-subs/application])
        cloudcheck-visible? (and (<sub [::ws-subs/has-feature? :cloudcheck-extracted-rules?])
                                 (<sub [::subs/plan-check-enabled-for-group?])
                                 (ff/enabled? :feature-flag.id/wizard-ai-precheck))
        bills-layout?       (ff/enabled? :feature-flag.id/wizard-estimate-costs)]
    [:div
     (when cloudcheck-visible?
       [cloudcheck-results tr])
     [:h5.mt-6.mb-5 (tr [:application-wizard/overview])]
     (if (or (<sub [::ws-subs/loading-config?]) (not (:db/id application)))
       [:div (cc/skeleton-text)]
       (if bills-layout?
         [:<>
          [:div.flex.flex-col.md:flex-row.md:items-start.gap-2
           [overview-view tr]
           [bills-view tr]]
          [bond-section-view tr]
          [submit-guard-results tr]]
         [:div.flex.flex-col.gap-2
          [overview-view tr]
          [bond-section-view tr]
          [submit-guard-results tr]]))]))
